import BadgeRounded from '@mui/icons-material/BadgeRounded';
import BlockRounded from '@mui/icons-material/BlockRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import KeyRounded from '@mui/icons-material/KeyRounded';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import MoreHorizRounded from '@mui/icons-material/MoreHorizRounded';
import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, unwrap } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { useImageSrc } from '../../../lib/images';
import { DataTable, SearchField, type Column } from '../../../components/DataTable';
import { EmptyState } from '../../../components/states';
import { StatusChip } from '../../../components/StatusChip';
import { initials, relativeTime } from '../../../lib/format';
import { useNotify } from '../../../lib/notify';
import { keys, type Staff } from '../api';
import { ResetPasswordDialog, StaffDialog } from './StaffDialogs';

const AVATAR_TONES = ['#4F46E5', '#0E9384', '#C2410C', '#7C3AED', '#0369A1', '#BE123C', '#047857'];

export function avatarTone(seed: string): string {
  let h = 0;
  for (const ch of seed) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_TONES[h % AVATAR_TONES.length] ?? '#4F46E5';
}

export function PersonAvatar({
  name,
  size = 36,
  imageUrl,
}: {
  name: string;
  size?: number;
  /** The person's photo (`imageUrl` from the API); initials without one. */
  imageUrl?: string | null;
}) {
  const tone = avatarTone(name);
  const { src } = useImageSrc(imageUrl);
  return (
    <Avatar
      src={src ?? undefined}
      alt={src ? name : undefined}
      sx={(t) => ({
        width: size,
        height: size,
        fontSize: size * 0.38,
        color: tone,
        bgcolor: `${tone}1F`,
        border: `1px solid ${tone}33`,
        // Lift the tone on dark backgrounds so the initials stay readable.
        ...t.applyStyles('dark', { color: `color-mix(in srgb, ${tone} 45%, #fff)` }),
      })}
    >
      {initials(name)}
    </Avatar>
  );
}

type Action = { kind: 'edit' | 'reset' | 'status' | 'sessions'; staff: Staff };

export function TeamTab({ onAdd }: { onAdd: () => void }) {
  const { can, me } = useAuth();
  const canManage = can('staff.manage');
  const qc = useQueryClient();
  const notify = useNotify();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'ALL' | 'ACTIVE' | 'DISABLED'>('ALL');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [menu, setMenu] = useState<{ el: HTMLElement; staff: Staff } | null>(null);
  const [action, setAction] = useState<Action | null>(null);

  const query = { page, pageSize, ...(q ? { q } : {}), ...(status !== 'ALL' ? { status } : {}) };
  const { data, isLoading } = useQuery({
    queryKey: [...keys.staff, 'list', query],
    queryFn: () => unwrap(api.GET('/staff', { params: { query } })),
    placeholderData: keepPreviousData,
  });

  const columns: Column<Staff>[] = [
    {
      key: 'name',
      header: 'Member',
      render: (s) => (
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
          <PersonAvatar name={s.name} imageUrl={s.imageUrl} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 650 }} noWrap>
              {s.name}
              {s.id === me?.user.id && (
                <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  {' '}
                  (you)
                </Box>
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {s.email ?? s.phone ?? '—'}
            </Typography>
          </Box>
        </Stack>
      ),
    },
    {
      key: 'roles',
      header: 'Roles',
      hideBelow: 'sm',
      render: (s) => (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {s.roles.map((r) => (
            <Chip
              key={r.id}
              size="small"
              label={r.name}
              variant="outlined"
              icon={s.isOwner && r.name === 'Owner' ? <BadgeRounded /> : undefined}
              color={s.isOwner && r.name === 'Owner' ? 'primary' : 'default'}
            />
          ))}
        </Stack>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (s) => (
        <Stack spacing={0.25} sx={{ alignItems: 'flex-start' }}>
          <StatusChip
            status={s.status === 'DISABLED' ? 'INACTIVE' : 'ACTIVE'}
            label={s.status === 'DISABLED' ? 'Deactivated' : 'Active'}
          />
          {s.mustChangePassword && s.status === 'ACTIVE' && (
            <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
              Temporary password
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      key: 'last',
      header: 'Last sign-in',
      hideBelow: 'md',
      render: (s) => (
        <Typography variant="body2" color={s.lastLoginAt ? 'text.primary' : 'text.secondary'}>
          {s.lastLoginAt ? relativeTime(s.lastLoginAt) : 'Never'}
        </Typography>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: 56,
      render: (s) =>
        canManage && (
          <IconButton
            size="small"
            aria-label={`Actions for ${s.name}`}
            onClick={(e) => {
              e.stopPropagation();
              setMenu({ el: e.currentTarget, staff: s });
            }}
          >
            <MoreHorizRounded fontSize="small" />
          </IconButton>
        ),
    },
  ];

  const open = (kind: Action['kind']) => {
    if (menu) setAction({ kind, staff: menu.staff });
    setMenu(null);
  };

  const target = action?.staff;
  const deactivating = target?.status === 'ACTIVE';

  return (
    <>
      <DataTable
        columns={columns}
        rows={data?.data}
        rowKey={(s) => s.id}
        loading={isLoading}
        page={data?.page}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
        onRowClick={canManage ? (s) => setAction({ kind: 'edit', staff: s }) : undefined}
        toolbar={
          <>
            <SearchField
              value={q}
              onChange={(v) => {
                setQ(v);
                setPage(1);
              }}
              placeholder="Search by name, email or phone…"
            />
            <Box sx={{ flex: 1 }} />
            <ToggleButtonGroup
              exclusive
              size="small"
              value={status}
              onChange={(_, v: typeof status | null) => {
                if (v) {
                  setStatus(v);
                  setPage(1);
                }
              }}
              aria-label="Filter by status"
              sx={{
                '& .MuiToggleButton-root': { px: 1.75, textTransform: 'none', fontWeight: 600 },
              }}
            >
              <ToggleButton value="ALL">All</ToggleButton>
              <ToggleButton value="ACTIVE">Active</ToggleButton>
              <ToggleButton value="DISABLED">Deactivated</ToggleButton>
            </ToggleButtonGroup>
          </>
        }
        empty={
          <EmptyState
            icon={<BadgeRounded />}
            title={q || status !== 'ALL' ? 'No one matches' : 'Just you so far'}
            body={
              q || status !== 'ALL'
                ? 'Try a different search or filter.'
                : 'Invite your staff so they can sell, receive stock and take payments.'
            }
            action={canManage && !q ? { label: 'Add staff', onClick: onAdd } : undefined}
          />
        }
      />

      <Menu
        anchorEl={menu?.el}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        <MenuItem onClick={() => open('edit')}>
          <ListItemIcon>
            <EditRounded fontSize="small" />
          </ListItemIcon>
          Edit details & roles
        </MenuItem>
        <MenuItem onClick={() => open('reset')}>
          <ListItemIcon>
            <KeyRounded fontSize="small" />
          </ListItemIcon>
          Reset password
        </MenuItem>
        <MenuItem onClick={() => open('sessions')}>
          <ListItemIcon>
            <LogoutRounded fontSize="small" />
          </ListItemIcon>
          Sign out everywhere
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        {menu?.staff.status === 'ACTIVE' ? (
          <Tooltip
            title={menu.staff.id === me?.user.id ? 'You can’t deactivate yourself' : ''}
            placement="left"
          >
            <span>
              <MenuItem
                onClick={() => open('status')}
                disabled={menu.staff.id === me?.user.id}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon sx={{ color: 'inherit' }}>
                  <BlockRounded fontSize="small" />
                </ListItemIcon>
                Deactivate
              </MenuItem>
            </span>
          </Tooltip>
        ) : (
          <MenuItem onClick={() => open('status')} sx={{ color: 'success.main' }}>
            <ListItemIcon sx={{ color: 'inherit' }}>
              <CheckCircleRounded fontSize="small" />
            </ListItemIcon>
            Reactivate
          </MenuItem>
        )}
      </Menu>

      {action?.kind === 'edit' && (
        <StaffDialog
          // The listed row, so a new photo shows while the dialog is open.
          staff={data?.data.find((s) => s.id === action.staff.id) ?? action.staff}
          onClose={() => setAction(null)}
        />
      )}
      {action?.kind === 'reset' && (
        <ResetPasswordDialog
          staff={action.staff}
          isSelf={action.staff.id === me?.user.id}
          onClose={() => setAction(null)}
        />
      )}
      <ConfirmDialog
        open={action?.kind === 'status'}
        title={`${deactivating ? 'Deactivate' : 'Reactivate'} ${action?.staff.name ?? ''}?`}
        body={
          deactivating
            ? 'They are signed out of every device right away and can’t sign in until reactivated. Their past work stays in your records.'
            : 'They can sign in again with their existing password and roles.'
        }
        confirmLabel={deactivating ? 'Deactivate' : 'Reactivate'}
        danger={deactivating}
        onClose={() => setAction(null)}
        onConfirm={async () => {
          if (!target) return;
          try {
            await unwrap(
              api.PUT('/staff/{id}/status', {
                params: { path: { id: target.id } },
                body: { status: deactivating ? 'DISABLED' : 'ACTIVE', version: target.version },
              }),
            );
            await qc.invalidateQueries({ queryKey: keys.staff });
            void qc.invalidateQueries({ queryKey: keys.subscriptionStatus });
            notify.success(`${target.name} ${deactivating ? 'deactivated' : 'reactivated'}`);
          } catch (e) {
            notify.error(e);
          }
        }}
      />
      <ConfirmDialog
        open={action?.kind === 'sessions'}
        title={`Sign ${target?.name ?? ''} out everywhere?`}
        body="Every device they are signed in on (web and desktop) is signed out. They can sign in again with their password."
        confirmLabel="Sign out everywhere"
        onClose={() => setAction(null)}
        onConfirm={async () => {
          if (!target) return;
          try {
            const r = await unwrap(
              api.POST('/staff/{id}/revoke-sessions', { params: { path: { id: target.id } } }),
            );
            notify.success(
              r.revokedSessions
                ? `Signed out of ${String(r.revokedSessions)} session${r.revokedSessions === 1 ? '' : 's'}`
                : `${target.name} had no active sessions`,
            );
          } catch (e) {
            notify.error(e);
          }
        }}
      />
    </>
  );
}
